import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── The half that was built and had no screen ───────────────────────────────────────────────────
// An audit of this module kept returning the same sentence: "complete server-side, unreachable from
// any screen". Eight features were in that state — routes written, services tested, and nothing in
// src/ calling them. A feature nobody can reach is indistinguishable from one that was never built,
// except that it also costs maintenance.
//
// Everything here is that recovery. No new server behaviour except where the audit found a defect:
//   · the reporting framework was one CURRENT value per client, so electing TFRS for PAEs this year
//     silently restated which framework last year's filed statements claimed — it is now per period,
//     and a closed period refuses to be restated;
//   · the foreign-currency tax-invoice gate was written, tested, and never called from the document
//     paths, so `currencyOf` accepted any three-letter code — it is wired now.
//
// Operator labels go through t('pa_headless_…'). Statutory Thai — book, statement, register and
// document names, RD form codes, statute references, unit names — is NOT translated: it is what the
// server prints on the document face, so it stays character-for-character identical in every locale
// and an operator reading the Chinese UI still sees which Thai document they are about to issue.
// Server-returned text (d.rule, d.why, d.note, warnings[], x.taxNote, account name_th/name_en, …) is
// DATA and is never wrapped in t().

const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const baht = (s: any) => (Number(s || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const plain = (n: any) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const toSatang = (v: string) => Math.round((Number(v) || 0) * 100);
const today = () => new Date().toISOString().slice(0, 10);

const btn: React.CSSProperties = {
  padding: '6px 11px', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const inp: React.CSSProperties = {
  padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};
const row: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 9 };
const amber: React.CSSProperties = {
  fontSize: 11.5, color: '#92400E', background: '#FEF6E7', border: '1px solid #F5D9A8',
  padding: '7px 10px', borderRadius: 8, lineHeight: 1.6,
};
const red: React.CSSProperties = {
  fontSize: 11.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9',
  padding: '7px 10px', borderRadius: 8, lineHeight: 1.6,
};
const rule: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.6, marginBottom: 9 };
const th: React.CSSProperties = { textAlign: 'left', fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '5px 8px', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { fontSize: 12, color: 'var(--text-primary)', padding: '5px 8px', borderTop: '1px solid var(--border)', verticalAlign: 'top' };
const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

function Section({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '10px 13px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <i className={`ti ti-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{subtitle}</span>
        </span>
      </button>
      {open && <div style={{ padding: '2px 13px 13px' }}>{children}</div>}
    </div>
  );
}
const Err = ({ e }: { e: string }) => e ? <div style={red}>{e}</div> : null;

/** #8 บัญชีทำการ — the route existed with no button. */
function WorkingAccount({ clientId, periodId }: { clientId: string; periodId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { api.axWorkingAccount(clientId, periodId).then(setD).catch((e: any) => setErr(e.message)); }, [clientId, periodId]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_wa_rule_a')}<b>{t('pa_headless_wa_rule_b')}</b>{t('pa_headless_wa_rule_c')}
      </div>
      {d && (
        <div style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.9 }}>
          {t('pa_headless_wa_income')} {baht(d.income)} · {t('pa_headless_wa_cost')} {baht(d.costOfSales)} · {t('pa_headless_wa_gross')} <b>{baht(d.grossProfit)}</b><br />
          {t('pa_headless_wa_expenses')} {baht(d.expenses)} · {t('pa_headless_wa_net')} <b>{baht(d.netProfit)}</b>
          {d.reconciles === false && <><br /><span style={{ color: '#B42318' }}>{t('pa_headless_wa_mismatch_name')} — {t('pa_headless_wa_mismatch')}</span></>}
        </div>
      )}
      <div style={{ ...row, marginTop: 9 }}>
        <button disabled={busy} style={btn} onClick={async () => {
          setBusy(true); setErr('');
          try { window.open(await api.axDocUrl('working-account.pdf', { clientId, periodId }), '_blank'); }
          catch (e: any) { setErr(e.message); } finally { setBusy(false); }
        }}><i className={busy ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> {t('pa_headless_wa_doc_name')} · {t('pa_headless_wa_doc')}</button>
      </div>
      <Err e={err} />
    </div>
  );
}

/** #15 the reporting framework — now a fact about the PERIOD. */
function Framework({ clientId, periodId, closed }: { clientId: string; periodId: string; closed: boolean }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { const r: any = await api.axNotes(clientId, periodId); setD(r.framework); } catch (e: any) { setErr(e.message); }
  }, [clientId, periodId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_fw_rule')}
        <br /><b>{t('pa_headless_fw_period')}</b>{t('pa_headless_fw_period_tail')}
      </div>
      <div style={row}>
        <select value={d?.framework || ''} disabled={closed} onChange={async e => {
          setErr('');
          try { await api.axSetFramework(clientId, periodId, e.target.value); await load(); } catch (ex: any) { setErr(ex.message); }
        }} style={{ ...inp, minWidth: 260, opacity: closed ? 0.6 : 1 }}>
          <option value="">{t('pa_headless_fw_none')}</option>
          <option value="npae">{t('pa_headless_fw_npae')}</option>
          <option value="pae">{t('pa_headless_fw_pae')}</option>
        </select>
        {d?.dbdForm && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('pa_headless_fw_dbd_form', { form: d.dbdForm })}</span>}
        {closed && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('pa_headless_fw_locked')}</span>}
      </div>
      {(d?.warnings || []).map((w: string, i: number) => <div key={i} style={amber}>{w}</div>)}
      <Err e={err} />
    </div>
  );
}

/** #23 / #97 the share register — data model and API existed, no screen. */
function Shareholders({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [asAt, setAsAt] = useState(today());
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState({ nameTh: '', holderKind: 'individual', idNumber: '', nationality: 'Thai', shares: '', paidPerShare: '', heldFrom: '' });
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setD(await api.axShareholders(clientId, asAt)); } catch (e: any) { setErr(e.message); } }, [clientId, asAt]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_sh_rule')}
      </div>
      <div style={row}>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_sh_as_at')}<br />
          <input type="date" value={asAt} onChange={e => setAsAt(e.target.value)} style={{ ...inp, width: 140 }} /></label>
        {d && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('pa_headless_sh_holders', { n: d.holders?.length || 0 })} · {t('pa_headless_sh_shares', { n: plain(d.totalShares) })}
          {d.formAvailable === false && <span style={{ color: '#92400E' }}> · {t('pa_headless_sh_form_name')} {t('pa_headless_sh_form_not_held')}</span>}
        </span>}
      </div>
      <div style={row}>
        <input placeholder={t('pa_headless_sh_name_ph')} value={f.nameTh} onChange={e => setF({ ...f, nameTh: e.target.value })} style={{ ...inp, width: 190 }} />
        <select value={f.holderKind} onChange={e => setF({ ...f, holderKind: e.target.value })} style={inp}>
          <option value="individual">{t('pa_headless_sh_individual')}</option><option value="juristic">{t('pa_headless_sh_juristic')}</option>
        </select>
        <input placeholder={t('pa_headless_sh_idno')} value={f.idNumber} onChange={e => setF({ ...f, idNumber: e.target.value })} style={{ ...inp, width: 140 }} />
        <input placeholder={t('pa_headless_sh_nationality')} value={f.nationality} onChange={e => setF({ ...f, nationality: e.target.value })} style={{ ...inp, width: 90 }} />
        <input placeholder={t('pa_headless_sh_shares_ph')} value={f.shares} onChange={e => setF({ ...f, shares: e.target.value })} style={{ ...inp, width: 110 }} />
        <input placeholder={t('pa_headless_sh_paid_per_share')} value={f.paidPerShare} onChange={e => setF({ ...f, paidPerShare: e.target.value })} style={{ ...inp, width: 120 }} />
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_sh_held_from')}<br />
          <input type="date" value={f.heldFrom} onChange={e => setF({ ...f, heldFrom: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <button style={btn} onClick={async () => {
          setErr('');
          try {
            await api.axAddShareholder(clientId, { ...f, shares: Number(f.shares), paidPerShare: Number(f.paidPerShare) });
            setF({ ...f, nameTh: '', idNumber: '', shares: '', paidPerShare: '' }); await load();
          } catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-plus" aria-hidden /> {t('pa_headless_sh_add')}</button>
      </div>
      {(d?.holders || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_sh_th_name')}</th><th style={th}>{t('pa_headless_sh_th_kind')}</th><th style={th}>{t('pa_headless_sh_idno')}</th><th style={th}>{t('pa_headless_sh_nationality')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_sh_th_shares')}</th><th style={{ ...th, textAlign: 'right' }}>%</th><th style={th}>{t('pa_headless_sh_held_from')}</th><th style={th} /></tr></thead>
          <tbody>
            {d.holders.map((h: any) => (
              <tr key={h.id}>
                <td style={td}>{h.name_th || h.nameTh}</td>
                <td style={td}>{(h.holder_kind || h.holderKind) === 'juristic' ? t('pa_headless_sh_juristic') : t('pa_headless_sh_individual')}</td>
                <td style={td}>{h.id_number || h.idNumber || '—'}</td>
                <td style={td}>{h.nationality || '—'}</td>
                <td style={num}>{plain(h.shares)}</td>
                <td style={num}>{d.totalShares ? ((Number(h.shares) / Number(d.totalShares)) * 100).toFixed(2) : '—'}</td>
                <td style={td}>{dmy(h.held_from || h.heldFrom)}</td>
                <td style={td}>
                  <button style={{ ...btn, padding: '3px 7px', fontSize: 11 }} onClick={async () => {
                    const to = window.prompt(t('pa_headless_sh_end_prompt'), today()); if (!to) return;
                    setErr('');
                    try { await api.axEndHolding(h.id, { heldTo: to }); await load(); } catch (e: any) { setErr(e.message); }
                  }}>{t('pa_headless_sh_end')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_headless_sh_empty')}</div>}
      <Err e={err} />
    </div>
  );
}

/** #21 / #22 the DBD e-Filing package. */
function EfilingPackage({ clientId, periodId }: { clientId: string; periodId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [unit, setUnit] = useState<any>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try {
      const [a, u]: any[] = await Promise.all([api.axEfilingPackage(clientId, periodId), api.axFsUnit(clientId, periodId).catch(() => null)]);
      setD(a); setUnit(u);
    } catch (e: any) { setErr(e.message); }
  }, [clientId, periodId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_pkg_rule')}
      </div>
      {unit && (
        <div style={row}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_headless_pkg_unit')}</span>
          <select value={unit.unit || ''} onChange={async e => {
            setErr('');
            try { await api.axSetFsUnit(clientId, periodId, e.target.value); await load(); } catch (ex: any) { setErr(ex.message); }
          }} style={inp}>
            {(unit.options || []).map((o: any) => <option key={o.key || o} value={o.key || o}>{o.th || o.label || o}</option>)}
          </select>
        </div>
      )}
      {(d?.slots || []).map((s: any) => (
        <div key={s.key} style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap', padding: '7px 0', borderTop: '1px solid var(--border)' }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', minWidth: 210 }}>{s.th || s.label}</span>
          <span style={{ fontSize: 11.5, color: s.fileId || s.declared ? '#166534' : 'var(--text-tertiary)' }}>
            {s.fileId ? t('pa_headless_pkg_file', { id: s.fileId }) : (s.declared ? t('pa_headless_pkg_declared') : t('pa_headless_pkg_not_attached'))}
          </span>
          {s.producible === false && <span style={{ ...amber, padding: '2px 8px' }}>{s.why || t('pa_headless_pkg_not_producible')}</span>}
          <button style={{ ...btn, padding: '3px 8px', fontSize: 11 }} onClick={async () => {
            const fid = window.prompt(t('pa_headless_pkg_file_prompt', { slot: s.th || s.label })); if (!fid) return;
            setErr('');
            try { await api.axSetPackageFile(clientId, periodId, { slot: s.key, fileId: fid }); await load(); } catch (e: any) { setErr(e.message); }
          }}>{t('pa_headless_pkg_attach')}</button>
        </div>
      ))}
      <div style={{ ...row, marginTop: 9 }}>
        <button style={btn} onClick={async () => {
          setErr('');
          try { await api.axDeclareBw(clientId, periodId); await load(); } catch (e: any) { setErr(e.message); }
        }}>{t('pa_headless_pkg_declare_bw')}</button>
      </div>
      {(d?.warnings || d?.notes || []).map((w: string, i: number) => <div key={i} style={amber}>{w}</div>)}
      <Err e={err} />
    </div>
  );
}

/** #65 account-level tax treatment — GET wrapper existed, no PATCH, no screen. */
function TaxTreatment({ clientId, periodId }: { clientId: string; periodId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setD(await api.axTaxTreatment(clientId, periodId)); } catch (e: any) { setErr(e.message); } }, [clientId, periodId]);
  useEffect(() => { load(); }, [load]);
  const set = async (id: string, v: string) => {
    setErr('');
    try { await api.axSetAccountTaxTreatment(id, { citDeductible: v }); await load(); } catch (e: any) { setErr(e.message); }
  };
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_cit_rule')}
      </div>
      {(d?.accounts || d?.rows || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_cit_th_account')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_cit_th_movement')}</th><th style={th}>{t('pa_headless_cit_th_treatment')}</th><th style={th}>{t('pa_headless_cit_th_note')}</th></tr></thead>
          <tbody>
            {(d.accounts || d.rows).map((a: any) => (
              <tr key={a.id}>
                <td style={td}>{a.code} {a.name_th || a.nameTh}</td>
                <td style={num}>{a.amount != null ? baht(a.amount) : '—'}</td>
                <td style={td}>
                  <select value={a.cit_deductible || a.citDeductible || ''} onChange={e => set(a.id, e.target.value)} style={{ ...inp, padding: '3px 6px', fontSize: 11.5 }}>
                    <option value="">{t('pa_headless_cit_undecided')}</option>
                    <option value="yes">{t('pa_headless_cit_yes')}</option>
                    <option value="no">{t('pa_headless_cit_no')}</option>
                    <option value="limited">{t('pa_headless_cit_limited')}</option>
                  </select>
                </td>
                <td style={{ ...td, fontSize: 11, color: 'var(--text-tertiary)' }}>{a.cit_limit_note || a.citLimitNote || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_headless_cit_empty')}</div>}
      {(d?.warnings || []).map((w: string, i: number) => <div key={i} style={amber}>{w}</div>)}
      <Err e={err} />
    </div>
  );
}

/** #56 the foreign-currency tax-invoice approval — built, tested, and never called until now. */
function FxApproval({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ currency: 'USD', approvalNo: '', approvedOn: '', effectiveFrom: '', effectiveTo: '' });
  const [check, setCheck] = useState<any>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_fx_rule_a')}<b>{t('pa_headless_fx_rule_b')}</b>{t('pa_headless_fx_rule_c')}
      </div>
      <div style={row}>
        <input placeholder="USD" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value.toUpperCase() })} style={{ ...inp, width: 80 }} />
        <input placeholder={t('pa_headless_fx_approval_no')} value={f.approvalNo} onChange={e => setF({ ...f, approvalNo: e.target.value })} style={{ ...inp, width: 170 }} />
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_fx_approved_on')}<br />
          <input type="date" value={f.approvedOn} onChange={e => setF({ ...f, approvedOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_fx_effective_from')}<br />
          <input type="date" value={f.effectiveFrom} onChange={e => setF({ ...f, effectiveFrom: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_fx_effective_to')}<br />
          <input type="date" value={f.effectiveTo} onChange={e => setF({ ...f, effectiveTo: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <button style={btn} onClick={async () => {
          setErr(''); setMsg('');
          try { await api.axAddFxApproval(clientId, f); setMsg(t('pa_headless_fx_recorded', { currency: f.currency })); setF({ ...f, approvalNo: '' }); }
          catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-plus" aria-hidden /> {t('pa_headless_fx_record')}</button>
        <button style={btn} onClick={async () => {
          setErr(''); setMsg('');
          try { setCheck(await api.axFxAllowed(clientId, f.currency, today())); } catch (e: any) { setErr(e.message); }
        }}>{t('pa_headless_fx_check', { currency: f.currency })}</button>
      </div>
      {check && <div style={check.allowed ? { ...amber, color: '#166534', background: '#E8F6EE', borderColor: '#BBE5CB' } : red}>{check.why || (check.allowed ? t('pa_headless_fx_allowed') : t('pa_headless_fx_not_allowed'))}</div>}
      {msg && <div style={{ fontSize: 12.5, color: '#166534' }}>{msg}</div>}
      <Err e={err} />
    </div>
  );
}

/** #58 the retention period and the destruction register — machinery existed with no route at all. */
function Retention({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setD(await api.arRetention(clientId)); } catch (e: any) { setErr(e.message); } }, [clientId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      <div style={row}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_headless_ret_label')}</span>
        <select value={d?.years || 7} onChange={async e => {
          setErr('');
          try { await api.arSetRetentionYears(Number(e.target.value)); await load(); } catch (ex: any) { setErr(ex.message); }
        }} style={inp}>
          <option value={5}>{t('pa_headless_ret_5y')}</option>
          <option value={6}>{t('pa_headless_ret_6y')}</option>
          <option value={7}>{t('pa_headless_ret_7y')}</option>
        </select>
      </div>
      {(d?.rows || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_ret_th_when')}</th><th style={th}>{t('pa_headless_ret_th_what')}</th><th style={th}>{t('pa_headless_ret_th_mode')}</th><th style={th}>{t('pa_headless_ret_th_reason')}</th><th style={th}>{t('pa_headless_ret_th_by')}</th><th style={th}>SHA-256</th></tr></thead>
          <tbody>
            {d.rows.map((r: any) => (
              <tr key={r.id}>
                <td style={td}>{new Date(Number(r.at) * 1000).toISOString().slice(0, 10)}</td>
                <td style={td}>{r.label || `${r.obj_table} ${String(r.obj_id).slice(0, 8)}`}{r.doc_no ? ` · ${r.doc_no}` : ''}</td>
                <td style={td}>{r.mode}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.reason}</td>
                <td style={td}>{r.by_name || '—'}</td>
                <td style={{ ...td, fontSize: 10.5, fontFamily: 'monospace' }}>{String(r.sha256 || '').slice(0, 12) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: '#166534' }}>{t('pa_headless_ret_none')}</div>}
      <Err e={err} />
    </div>
  );
}

/** #78 the first general meeting, and every twelve months after. */
function Agm({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.arAgm(clientId).then(setD).catch((e: any) => setErr(e.message)); }, [clientId]);
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      {d?.why ? <div style={amber}>{d.why}</div> : null}
      {d && (
        <div style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.9 }}>
          {d.registeredOn ? <>{t('pa_headless_agm_registered')} {dmy(d.registeredOn)} · {t('pa_headless_agm_first_due')} <b>{dmy(d.firstDueBy)}</b><br /></> : null}
          {d.lastHeldOn ? <>{t('pa_headless_agm_last_held')} {dmy(d.lastHeldOn)}<br /></> : null}
          {d.nextDueBy
            ? <>{t('pa_headless_agm_next_due')} <b style={{ color: d.overdue ? '#B42318' : 'var(--text-primary)' }}>{dmy(d.nextDueBy)}</b>{d.overdue ? ` · ${t('pa_headless_agm_overdue')}` : ''}
                <span style={{ color: 'var(--text-tertiary)' }}> — {d.basis}</span></>
            : null}
        </div>
      )}
      <Err e={err} />
    </div>
  );
}

/** #91 s.54 — who owes the tax when withholding went wrong. */
function WhtLiability({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.arWhtLiability(clientId, year).then(setD).catch((e: any) => setErr(e.message)); }, [clientId, year]);
  const atRisk = (d?.payerAlone || 0) + (d?.joint || 0);
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      <div style={row}>
        <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inp, width: 90 }} />
        {d && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('pa_headless_wht_settled', { n: d.settledCount })} · {t('pa_headless_wht_payer_alone')} <b style={{ color: d.payerAlone ? '#B42318' : 'inherit' }}>{plain(d.payerAlone)}</b>
          {' '}· {t('pa_headless_wht_joint')} <b style={{ color: d.joint ? '#92400E' : 'inherit' }}>{plain(d.joint)}</b>
        </span>}
      </div>
      {atRisk > 0 && <div style={red}>{t('pa_headless_wht_at_risk', { amount: plain(atRisk) })} {d.note}</div>}
      {(d?.rows || []).filter((r: any) => r.liable !== 'settled').length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_wht_th_paid')}</th><th style={th}>{t('pa_headless_wht_th_payee')}</th><th style={th}>{t('pa_headless_wht_th_form')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_wht_th_gross')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_wht_th_withheld')}</th>
            <th style={th}>{t('pa_headless_wht_th_who')}</th><th style={th}>{t('pa_headless_wht_th_why')}</th></tr></thead>
          <tbody>
            {d.rows.filter((r: any) => r.liable !== 'settled').map((r: any) => (
              <tr key={r.id}>
                <td style={td}>{dmy(r.payDate)}</td>
                <td style={td}>{r.payeeName}</td>
                <td style={td}>{String(r.form).toUpperCase().replace('PND', 'PND.')}</td>
                <td style={num}>{plain(r.gross)}</td>
                <td style={num}>{r.wasWithheld ? plain(r.withheld) : '—'}</td>
                <td style={{ ...td, color: r.liable === 'payer_alone' ? '#B42318' : '#92400E' }}>{r.liableLabel}</td>
                <td style={{ ...td, fontSize: 11, color: 'var(--text-tertiary)' }}>{r.why}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: '#166534' }}>{t('pa_headless_wht_all_clear')}</div>}
      <Err e={err} />
    </div>
  );
}

/** #94 the employer's own social-security registration. */
function EmployerSso({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState<any>({ accountNo: '', branchCode: '', registeredOn: '', form101FiledOn: '', office: '', firstEmployeeOn: '' });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try {
      const r: any = await api.arEmployerSso(clientId); setD(r);
      if (r.row) setF({ accountNo: r.row.accountNo, branchCode: r.row.branchCode, registeredOn: r.row.registeredOn,
        form101FiledOn: r.row.form101FiledOn, office: r.row.office, firstEmployeeOn: r.row.firstEmployeeOn });
    } catch (e: any) { setErr(e.message); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_sso_rule_a')}<b>{t('pa_headless_sso_rule_b')}</b>{t('pa_headless_sso_rule_c')}
      </div>
      {d?.gap && <div style={red}>{d.gap}</div>}
      <div style={row}>
        <input placeholder={t('pa_headless_sso_account_no')} value={f.accountNo} onChange={e => setF({ ...f, accountNo: e.target.value })} style={{ ...inp, width: 180 }} />
        <input placeholder={t('pa_headless_sso_branch')} value={f.branchCode} onChange={e => setF({ ...f, branchCode: e.target.value })} style={{ ...inp, width: 80 }} />
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_sso_registered_on')}<br />
          <input type="date" value={f.registeredOn} onChange={e => setF({ ...f, registeredOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_sso_form_101')}<br />
          <input type="date" value={f.form101FiledOn} onChange={e => setF({ ...f, form101FiledOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <input placeholder={t('pa_headless_sso_office')} value={f.office} onChange={e => setF({ ...f, office: e.target.value })} style={{ ...inp, width: 170 }} />
        <button style={btn} onClick={async () => {
          setErr(''); setMsg('');
          try { await api.arSaveEmployerSso(clientId, f); setMsg(t('pa_headless_sso_saved')); await load(); } catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-device-floppy" aria-hidden /> {t('pa_headless_sso_save')}</button>
        {msg && <span style={{ fontSize: 12, color: '#166534' }}>{msg}</span>}
      </div>
      {d && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        {t('pa_headless_sso_employees', { n: d.employees })}{d.employeesWithoutSsoId ? ` · ${t('pa_headless_sso_no_number', { n: d.employeesWithoutSsoId })}` : ''}
      </div>}
      <Err e={err} />
    </div>
  );
}

/** #92 an upward revaluation, and the two bases it creates. */
function AssetBases({ clientId, periodId }: { clientId: string; periodId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setD(await api.arAssetBases(clientId)); } catch (e: any) { setErr(e.message); } }, [clientId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_rev_rule_a')}<b>{t('pa_headless_rev_rule_b')}</b>{t('pa_headless_rev_rule_c')}
      </div>
      {(d?.rows || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_rev_th_asset')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_rev_th_cost')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_rev_th_book')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_rev_th_tax')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_rev_th_reval')}</th><th style={th} /></tr></thead>
          <tbody>
            {d.rows.map((r: any) => (
              <tr key={r.assetId}>
                <td style={td}>{r.name}</td>
                <td style={num}>{baht(r.costSatang)}</td>
                <td style={num}>{baht(r.bookBasisSatang)}</td>
                <td style={num}>{baht(r.taxBasisSatang)}</td>
                <td style={{ ...num, color: r.divergent ? '#92400E' : 'var(--text-tertiary)' }}>{r.revaluationGainSatang ? baht(r.revaluationGainSatang) : '—'}</td>
                <td style={td}>
                  <button style={{ ...btn, padding: '3px 7px', fontSize: 11 }} onClick={async () => {
                    const after = window.prompt(t('pa_headless_rev_amount_prompt', { name: r.name }), String(r.bookBasisSatang / 100)); if (!after) return;
                    const on = window.prompt(t('pa_headless_rev_date_prompt'), today()); if (!on) return;
                    const basis = window.prompt(t('pa_headless_rev_basis_prompt')); if (!basis) return;
                    setErr('');
                    try {
                      const x: any = await api.arRevalue(clientId, { assetId: r.assetId, revaluedOn: on, bookAfter: toSatang(after), basis, periodId });
                      window.alert(x.taxNote); await load();
                    } catch (e: any) { setErr(e.message); }
                  }}>{t('pa_headless_rev_revalue')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_headless_rev_empty')}</div>}
      {d?.note && <div style={amber}>{d.note}</div>}
      <Err e={err} />
    </div>
  );
}

export default function Headless({ clientId, periodId, periodClosed }:
  { clientId: string; periodId: string; periodClosed: boolean }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
        {t('pa_headless_module_title')}
      </div>
      <Section title={`${t('pa_headless_sec_wa_name')} · ${t('pa_headless_sec_wa')}`} subtitle={t('pa_headless_sec_wa_sub')}>
        <WorkingAccount clientId={clientId} periodId={periodId} />
      </Section>
      <Section title={`${t('pa_headless_sec_fw_name')} · ${t('pa_headless_sec_fw')}`} subtitle={t('pa_headless_sec_fw_sub')}>
        <Framework clientId={clientId} periodId={periodId} closed={periodClosed} />
      </Section>
      <Section title={`${t('pa_headless_sec_sh_name')} · ${t('pa_headless_sec_sh')}`} subtitle={t('pa_headless_sec_sh_sub')}>
        <Shareholders clientId={clientId} />
      </Section>
      <Section title={t('pa_headless_sec_pkg')} subtitle={t('pa_headless_sec_pkg_sub')}>
        <EfilingPackage clientId={clientId} periodId={periodId} />
      </Section>
      <Section title={t('pa_headless_sec_cit')} subtitle={t('pa_headless_sec_cit_sub')}>
        <TaxTreatment clientId={clientId} periodId={periodId} />
      </Section>
      <Section title={`${t('pa_headless_sec_fx_name')} · ${t('pa_headless_sec_fx')}`} subtitle={t('pa_headless_sec_fx_sub')}>
        <FxApproval clientId={clientId} />
      </Section>
      <Section title={t('pa_headless_sec_ret')} subtitle={t('pa_headless_sec_ret_sub')}>
        <Retention clientId={clientId} />
      </Section>
      <Section title={t('pa_headless_sec_agm')} subtitle={t('pa_headless_sec_agm_sub')}>
        <Agm clientId={clientId} />
      </Section>
      <Section title={t('pa_headless_sec_wht')} subtitle={t('pa_headless_sec_wht_sub')}>
        <WhtLiability clientId={clientId} />
      </Section>
      <Section title={t('pa_headless_sec_sso')} subtitle={t('pa_headless_sec_sso_sub')}>
        <EmployerSso clientId={clientId} />
      </Section>
      <Section title={t('pa_headless_sec_rev')} subtitle={t('pa_headless_sec_rev_sub')}>
        <AssetBases clientId={clientId} periodId={periodId} />
      </Section>
    </div>
  );
}
